// D2TalkingHeadAvatar.js
import React, {
    useState,
    useEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
  } from 'react';

  const AVATURN_AVATAR_URL =
    'https://cdn.jsdelivr.net/gh/met4citizen/TalkingHead@main/avatars/avaturn.glb';

  function isReadyPlayerAvatar(url) {
    return typeof url === 'string' && /readyplayer\.me/i.test(url);
  }

  function buildAvatarUrl(baseAvatar) {
    const rawAvatar =
      typeof baseAvatar === 'string' && baseAvatar.trim()
        ? baseAvatar.trim()
        : AVATURN_AVATAR_URL;

    if (isReadyPlayerAvatar(rawAvatar)) {
      return AVATURN_AVATAR_URL;
    }

    return rawAvatar;
  }

  function pickBrowserVoice(voices) {
    if (!Array.isArray(voices) || voices.length === 0) {
      return null;
    }

    const preferredVoices = voices.filter((voice) =>
      String(voice.lang || '').toLowerCase().startsWith('en-')
    );
    return preferredVoices[0] || voices[0] || null;
  }

  function speakWithBrowserTts(text) {
    if (
      typeof window === 'undefined' ||
      !window.speechSynthesis ||
      typeof window.SpeechSynthesisUtterance !== 'function' ||
      !text
    ) {
      return;
    }

    const utterance = new window.SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    const selectedVoice = pickBrowserVoice(window.speechSynthesis.getVoices?.() || []);
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }

    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  }

  const D2TalkingHeadAvatar = forwardRef((props, ref) => {
    const avatarRef = useRef(null);
    const [loadingText, setLoadingText] = useState('Loading...');
    const headRef = useRef(null);
  
    const baseAvatar =
      localStorage.getItem('avatar') ||
      AVATURN_AVATAR_URL;

    const avatarUrl = buildAvatarUrl(baseAvatar);
  
    // Expose methods to parent
    useImperativeHandle(ref, () => ({
      speakText: async (text) => {
        if (!text) {
          return;
        }

        if (headRef.current && typeof headRef.current.speakText === 'function') {
          try {
            if (headRef.current.audioCtx?.state === 'suspended') {
              await headRef.current.audioCtx.resume();
            }
            await headRef.current.speakText(text);
            return;
          } catch (error) {
            console.error('TalkingHead speech failed, using browser TTS:', error);
          }
        }

        speakWithBrowserTts(text);
      },
    }));
  
    useEffect(() => {
        let isMounted = true;
      
        import(
          /* webpackIgnore: true */
          "https://cdn.jsdelivr.net/gh/met4citizen/TalkingHead@1.4/modules/talkinghead.mjs"
        )
          .then((module) => {
            if (!isMounted) return;
            const { TalkingHead } = module;
      
            const head = new TalkingHead(avatarRef.current, {
              ttsEndpoint:
                'https://eu-texttospeech.googleapis.com/v1beta1/text:synthesize',
              ttsApikey: 'AIzaSyDSkk-2DrYGYe1IStMudAx_GJz9Va7kT6Y',
              lipsyncModules: ['en', 'fi'],
              cameraView: 'upper',
            });
      
            headRef.current = head;
      
            head
              .showAvatar(
                {
                  url: avatarUrl,
                  body: 'F',
                  avatarMood: 'neutral',
                  ttsLang: 'en-GB',
                  ttsVoice: 'en-US-Wavenet-F',
                  lipsyncLang: 'en',
                },
                (ev) => {
                  if (ev.lengthComputable) {
                    const val = Math.min(
                      100,
                      Math.round((ev.loaded / ev.total) * 100)
                    );
                    setLoadingText('Loading ' + val + '%');
                  }
                }
              )
              .then(() => setLoadingText(''))
              .catch((err) => {
                console.error('Error loading avatar:', err);
                setLoadingText('Error loading avatar');
              });
          })
          .catch((err) => {
            console.error('Failed to load TalkingHead module:', err);
            setLoadingText('Error loading module');
          });
      
        return () => {
          isMounted = false;
        };
      }, [avatarUrl]);
  
    return (
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        <div ref={avatarRef} style={{ width: '100%', height: '100%' }} />
        {loadingText && (
          <div
            style={{
              position: 'absolute',
              bottom: 10,
              left: 10,
              right: 10,
              textAlign: 'center',
              color: 'white',
            }}
          >
            {loadingText}
          </div>
        )}
      </div>
    );
  });
  
  export default D2TalkingHeadAvatar;
